//
// Form submit
//

import $ from 'jquery'
import { defer } from 'lodash-es'
import { handleDataFocus } from '~/common/behaviors/form_focus'
import { debounce } from 'throttle-debounce'
import { disableBProgressBarTemporarily } from './progress_bar'
import classNames from 'classnames'
import {
  RESHOW_AJAX_TOOLTIP_SELECTOR,
  reshowAjaxTooltipFor,
} from '~/lib/reshow_ajax_tooltip'
import { measure, mutate } from '~/common/helpers/layout_batch'
import {
  saveMayBeInFlight,
  saveTriggered,
} from '~/common/helpers/unload_warning'

// Submit the form on a change event.
// You can add data-behavior="submit-on-change" anywhere inside the form and it will submit the form anytime any inputs fire a change event.
// If there's a subpart of the form where you don't want change events to trigger this behavior, you can just add the class "prevent-submit-on-change" to the element.
// Any descendants of that element will not trigger this behavior.
// You can also specify:
//  data-remote-submit-url: When set, will submit the form to the specified url using ajax. If not set, we will submit the form normally using $form.submit()

// Warn before closing the tab while a save is in progress. The state behind this lives in
// ~/common/helpers/unload_warning, because AjaxHelper has to be able to stand the warning down
// before a redirect it asked for itself — see the comment there for why $.active alone cannot
// tell a save in flight from one the app already has the answer to.
window.addEventListener('beforeunload', (e) => {
  if ($.active > 0 && saveMayBeInFlight()) e.preventDefault()
})

const submitOnChangeHandler = debounce(300, function (e) {
  // We dispatch a change() event in various places in onDomChangedListeners (e.g on inputs with data-checkbox-toggle).
  // Let's not submit the form if isAjaxUpdating is true because otherwise you can easily get into an infinite loop (on a real change event, we submit, the submit rerenders the form that has one checkbox toggle, onDomChanged fires for that checkbox toggle, triggering a change event, which in turn submits the form again and so on).
  if (window.Simplero?.state?.isAjaxUpdating) return

  const $this = $(this)
  const $form = $this.closest('form')
  const prevent =
    $(e.target).closest('.prevent-submit-on-change').closest('form')[0] ===
      $form[0] || !e.target.isConnected
  if (!prevent) {
    if ($form.data('global') === false) disableBProgressBarTemporarily()
    // Disable inputs inside .prevent-submit-on-change sections so they don't interfere with
    // required-input validation or serialization (they have their own save mechanism or are display-only).
    const $previewInputs = $form
      .find('.prevent-submit-on-change :input')
      .prop('disabled', true)

    saveTriggered()

    if ($(this).data('remote-submit-url')) {
      const formData = new FormData($form[0])
      formData.delete('_method')
      $previewInputs.prop('disabled', false)
      $.sajax({
        url: $this.data('remote-submit-url'),
        type: $this.data('ajax-method') || 'POST',
        data: formData,
        processData: false,
        contentType: false,
      })
    } else if (window.Rails && $form.data('remote')) {
      const form = $form[0]
      if (form.requestSubmit) form.requestSubmit()
      else form.submit()
      $previewInputs.prop('disabled', false)
    } else {
      $form.trigger('submit')
      $previewInputs.prop('disabled', false)
    }
  }
})

$(document).on(
  'change input color-swatch-picker:save wysiwyg-changed',
  '[data-behavior~=submit-on-change]',
  submitOnChangeHandler
)

// Prevent submitOnChangeHandler from firing during delete operations
$(document).on(
  'click',
  '[data-method="delete"][data-behavior~="ajax-update"]',
  function () {
    // Cancel any pending debounced form submissions
    submitOnChangeHandler.cancel({ upcomingOnly: true })
  }
)

// Submit the enclosing form's current state to an endpoint on click, then apply the
// JSON response via ajax-update. Lets structural ops (add/remove installment) re-render
// from the user's unsaved edits instead of reverting them. ENG-30226.
$(document).on('click', '[data-behavior~=submit-form-to]', function (e) {
  e.preventDefault()
  const $el = $(this)
  // Custom key (not data-confirm) so Rails UJS doesn't also bind its own confirm dialog.
  const confirmMsg = $el.data('confirmMessage')
  if (confirmMsg && !window.confirm(confirmMsg)) return

  const $form = $el.closest('form')
  const formData = new FormData($form[0])
  formData.delete('_method')
  // Read the verb from a custom key (not data-method) so Rails UJS doesn't hijack the click
  // and fire its own method-spoofed navigation.
  $.sajax({
    url: $el.attr('href') || $el.data('url'),
    type: $el.data('formMethod') || 'POST',
    data: formData,
    processData: false,
    contentType: false,
    element: this,
  })
})

window.onPageChange('submit on page load', () =>
  $('[data-behavior~=submit-on-page-load]').each(function () {
    setTimeout(() => $(this).submit(), 30)
  })
)

// Save change immediately when changing the state of a switch, like Do Not Contact
$(document).on('change', '[data-behavior~=switch-save]', function (e) {
  const $el = $(e.target)
  const params = {}
  params[$el.attr('name')] = $el.is(':checked')
  $.ajax({
    url: $el.data('url'),
    data: params,
    type: 'PATCH',
    error(xhr, error, message) {
      alert(`There was a problem saving your change: ${message}. Sorry :(`)
    },
    success(data) {
      if ($el.data('behavior').match(/ajax-update/)) {
        window.AjaxHelper.doUpdate(data)
      }
    },
  })
})

$(document).on('click', '[data-behavior~=save]', function (e) {
  // TODO: is this used?? notify-usage candidate
  e.preventDefault()
  $('.page-body-inner form').submit()
})

//#
// Image and link popup
//

$(document).on('click', '[data-behavior=image-popup]', function (e) {
  e.stopPropagation()
  e.stopImmediatePropagation()
  e.preventDefault()

  const a = $(e.target).closest('a')

  const src = a.attr('href')
  let width = a.data('width')
  let height = a.data('height')
  const max_width = $(window).width() * 0.8
  const max_height = $(window).height() * 0.7

  if (width > max_width) {
    height = (height * max_width) / width
    width = max_width
  }
  if (height > max_height) {
    width = (width * max_height) / height
    height = max_height
  }

  const img = $('<img></img>').attr({
    src,
    width: '100%',
    height: '100%',
  })

  const div = $('<div></div>')
    .addClass('modal fade modal-image')
    .append(
      $('<div></div>')
        .addClass('modal-dialog')
        .css({ width, height })
        .append(img)
    )

  $('#modal-container .modal-image').remove()
  $('#modal-container').append(div)
  div.on('shown.bs.modal', () =>
    $(document).on(
      'keydown.modal-image',
      (e) => e.which === 27 && div.modal('hide')
    )
  )
  div.on('hidden.bs.modal', () => $(document).off('keydown.modal-image'))
  div.modal()
})

$(document).on(
  'click',
  'a.popup, a[rel=popup], a[data-behavior~=popup], .popup a:not([data-behavior~=only-anchor])',
  function () {
    if (!$(this).closest('.jp-audio').length) {
      this.setAttribute('target', '_blank')
    }
  }
)

$(document).on('click', '.popup-external a', function (e) {
  const link = $(e.target).closest('a')
  if (
    this.href.match(/:/) &&
    !this.href.match(`^${window.location.origin}/`) &&
    !link.attr('onclick')
  ) {
    e.preventDefault()
    window.open(this.href)
  }
})

$(document).on('click', 'a[data-behavior~=disable-on-update]', function (e) {
  const elementToDisable = $(e.target).closest('a').data().disableElement
  $(elementToDisable).append('<div class="mask"></div>')
})

$(document).on('click', 'a[data-behavior~=disable-on-click]', (e) => {
  const $target = $(e.target).closest('a')
  const $elementToDisable = $target.data('disable-target-selector')
    ? $($target.data('disable-target-selector'))
    : $target
  $elementToDisable.addClass('disabled').attr('disabled', true)
})

$(document).on('ajax:success', RESHOW_AJAX_TOOLTIP_SELECTOR, function () {
  reshowAjaxTooltipFor(this)
})

//#
// Space follow and like
// Used anywhere else?
//

$(document).on('click', '[data-behavior=like]', function (event) {
  event.preventDefault()
  const $target = $(event.target)
  $.ajax({
    url: $target.attr('href'),
    success(data) {
      const html = $(data.likes)
      $target.closest('.likes-container').replaceWith(html)
      html.find('span').effect('highlight', {}, 1000)
    },
    error() {
      alert(
        "Sorry couldn't save your like, please come back and try again soon."
      )
    },
  })
})

$(document).on(
  'ajax:success',
  '[data-behavior=follow]',
  function (event, data) {
    const $target = $(event.target)
    const html = $(data.follows)
    $target.closest('.follows-container').replaceWith(html)
    html.find('span').effect('highlight', {}, 1000)
  }
)

$(document).on('ajax:error', '[data-behavior=follow]', () =>
  alert(
    "Sorry couldn't save your request, please come back and try again soon."
  )
)

$(document).on('click', '[data-behavior~=link]', function (e) {
  // TODO: is this used?? notify-usage candidate
  e.preventDefault()
  window.Turbolinks.visit(
    $(e.target).closest('[data-behavior~=link]').data('to')
  )
})

// Table row select checkboxes

$(document).on(
  'click',
  'input:checkbox[data-behavior~=select-all]',
  function () {
    $(this)
      .closest('table')
      .find('input:checkbox[data-behavior~=select-row]')
      .prop('checked', this.checked)
  }
)

$(document).on(
  'click',
  'input:checkbox[data-behavior~=select-row]',
  function () {
    const all_checked =
      $(this)
        .closest('table')
        .find('input:checkbox[data-behavior~=select-row]:not(:checked)')
        .length === 0
    $(this)
      .closest('table')
      .find('input:checkbox[data-behavior~=select-all]')
      .prop('checked', all_checked)
  }
)

//#
// Date picker
//

$(document).on('click', '[data-behavior~=datepicker]', (event) =>
  event.preventDefault()
)

window.onDomChanged('[data-behavior~=datepicker]', function () {
  if (!this.datetimepicker) return

  const options = this.data('options') || {}

  this.datetimepicker({
    format: 'YYYY-MM-DD',
    pickTime: false,
    showToday: true,
    icons: {
      time: 'fa fa-clock-o',
      date: 'fa fa-calendar',
      up: 'fa fa-caret-up',
      down: 'fa fa-caret-down',
    },
    ...options,
  })
  refocusIfFocused(this)
})

window.onDomChanged('[data-behavior~=datetimepicker]', function () {
  if (!this.datetimepicker) return
  const time_format_24h = this.parents('body').data('time-format-24h')
  let format = 'YYYY-MM-DD hh:mm a'
  if (time_format_24h) {
    format = 'YYYY-MM-DD HH:mm'
  }
  this.datetimepicker({
    format,
    layout: this.data('datepicker-layout') || 'vertical',
    timeZone: $('body').data('time-zone'),
    showToday: true,
    containerSelector: this.data('datepicker-container-selector') || 'body',
    noLessZeroTop: true,
    focusOnShow: false,
    icons: {
      time: 'fa fa-clock-o',
      date: 'fa fa-calendar',
      up: 'fa fa-caret-up',
      down: 'fa fa-caret-down',
    },
  })
  refocusIfFocused(this)
})

function refocusIfFocused(elm) {
  if (document.activeElement === elm[0]) elm.focus()
}

const datepicker_save = function (e) {
  let left, left1
  const $target = $(e.target)
  const data = {}
  data[(left = $target.data('param')) != null ? left : 'date'] = e.date
    ? e.date.format('YYYY-MM-DD hh:mm a')
    : ''
  $.ajax({
    url: $target.attr('href'),
    type: (left1 = $target.data('method')) != null ? left1 : 'POST',
    data,
    success(data) {
      $target.data('DateTimePicker').hide()
      window.AjaxHelper.doUpdate(data)
    },
    error(xhr, text, error) {
      $target.data('DateTimePicker').hide()
      window.AjaxHelper.error(error)
    },
  })
}

$(document).on('dp.save', '[data-behavior~=datepicker-save]', datepicker_save)

// Load moment and chrono only when really needed
let moment
let chrono

const importMoment = async () => {
  if (!moment) {
    const momentModule = await import('moment')
    moment = momentModule.default
  }
}

const importChrono = async () => {
  if (!chrono) {
    const chronoModule = await import('chrono-node')
    chrono = chronoModule
  }
}

const datePickerSelectors =
  '[data-behavior~=datetimepicker], [data-behavior~=datepicker]'

window.onDomChanged(datePickerSelectors, function () {
  importMoment()
  importChrono()
})

// Enable datetimepicker to parse natural language dates,
// such as "next week at 2pm"
$(document).on('keyup', datePickerSelectors, async (event) => {
  if (!$(event.target).data('DateTimePicker')) return
  if (!moment || !chrono) return
  if (event.key === 'Escape') return

  // chrono will try to parse the text into a date
  // if it can't, it will return null
  const parsedDate = chrono.parseDate(event.target.value)
  if (parsedDate) {
    const currentVal = event.target.value
    const cursorPosition = event.target.selectionStart
    const formatted = moment(parsedDate).format()
    $(event.target).data('DateTimePicker').setDate(formatted)
    $(event.target).data('DateTimePicker').show()
    event.target.value = currentVal

    // Preserve cursor position instead of just focusing
    if (event.target === document.activeElement) {
      event.target.setSelectionRange(cursorPosition, cursorPosition)
    }
  }
})

// Ensure that input fields remain with the correct format
// even if the user was typing natural language dates.
$(document).on('blur', datePickerSelectors, async (event) => {
  if (!$(event.target).data('DateTimePicker')) return
  if (!moment) return

  let input = event.target

  if (input.tagName.toLowerCase() !== 'input') {
    input = input.find('input')
  }

  if (!input) return

  const currentVal = input.value
  if (currentVal.length > 0) {
    // Ensure the input ends with the format set on the picker
    const date = $(event.target).data('DateTimePicker').date
    const format = $(event.target).data('DateTimePicker').format
    const formattedValue = moment(date).format(format)

    // Only update if the formatted value is different to avoid unnecessary cursor jumping
    if (input.value !== formattedValue) {
      input.value = formattedValue
    }
  }
})

//#
// Time picker
//

$(document).on(
  'click',
  '.input-group-addon:has([data-behavior~=timepicker_icon])',
  function () {
    $(this).closest('.controls').find('input.time-mm-hh').focus()
  }
)

//#
// Forms
//

$(document).on('click', 'a[data-personalization-tag]', function (event) {
  let ace
  event.preventDefault()
  const $this = $(this)
  const target = $($this.data('personalization-target'))
  const tag = $this.data('personalization-tag')
  const targetEl = target[0]
  if (targetEl && targetEl.__textMessageEditor) {
    targetEl.__textMessageEditor.insertToken(tag)
  } else if (targetEl && targetEl.__codeEditor) {
    targetEl.__codeEditor.insertAtCursor(tag)
  } else if ((ace = target.data('ace'))) {
    ace.editor.ace.insert(tag)
    ace.editor.ace.focus()
  } else {
    targetEl.value =
      targetEl.value.substr(0, targetEl.selectionStart) +
      tag +
      targetEl.value.substr(targetEl.selectionEnd, targetEl.value.length)
    target.trigger('change')
    target.focus()
  }
})

$(document).on(
  'shown.bs.modal',
  '.modal[data-behavior~=modal-with-form]',
  function (event) {
    // Make sure it's the modal, and not a tab inside the modal, that's shown.
    if ($(event.target).hasClass('modal')) {
      const form_with_focus = $(event.target).parents('form[data-focus]')
      if (form_with_focus.length > 0) {
        handleDataFocus(form_with_focus)
      } else {
        $(this)
          .find(':input:visible:enabled:not(.close):not(.no-autofocus):first')
          .focus()
      }
    }
  }
)

//#
// Revealing and hiding elements
//

// link: data-behavior="revealer" data-target="selector"
// rel=revealer-container will be hidden when clicked
$(document).on('click', '[data-behavior~=revealer]', function (e) {
  e.preventDefault()
  const $this = $(this)
  const first_input = $($this.attr('data-target'))
    .show()
    .find('input:visible,textarea:visible')
    .first()
  $this.closest('[rel=revealer-container]').hide()
  defer(() => first_input.focus())
})

$(document).on('click', '[data-behavior~=revealer-inverse]', function (e) {
  e.preventDefault()
  const $target = $(this).closest($(this).attr('data-target'))
  $target.hide()
  $target.parent().find('[rel=revealer-container]').show()
})

$(document).on('click', '[data-behavior=hide]', function (e) {
  e.preventDefault()
  $($(this).attr('data-target')).hide()
})

// Hidden products/lists/etc.
$(document).on('click', '[data-behavior~=show-hidden-objects]', function (e) {
  e.preventDefault()
  $($(e.target).attr('data-target') + '.start-hidden').toggle()
})

// Toggle folders in theme file editor
//
// data-target = selector for the thing to toggle
// data-focus  = selector for element to focus after showing
// sets class 'showing' on the link with data-behavior=toggle when the target is showing
//
$(document).on('click', '[data-behavior~=toggle]', function (e) {
  e.preventDefault()
  const target = $($(e.currentTarget).attr('data-target'))
  if (target.hasClass('start-hidden')) {
    target.removeClass('start-hidden')
  } else {
    target.toggle()
  }
  if (target.is(':visible')) {
    const focus = $(e.target).attr('data-focus')
    if (focus != null) {
      $(focus).focus()
    }
  }
  $(e.target).closest('[data-behavior~=toggle]').toggleClass('showing')
})

// Next row has details
$(document).on('click', 'a[data-behavior=show-details-next-row]', function (e) {
  e.preventDefault()
  $(this).closest('tr').next().toggle()
  $(this).children('.toggle-link').toggle()
})

$(document).on('keyup', '[data-behavior=reveal-on-change]', function (e) {
  const elm = $(e.target)
  $(elm.data('target')).toggle(elm.val() !== elm.data('original-value'))
})

//#
// Tooltips & Clickover
//

function showTooltip(
  $tooltip,
  $configEl,
  { title, trigger, placement, delay }
) {
  if (!$configEl) $configEl = $tooltip
  if (!title) title = $configEl.attr('title') || $configEl.data('title')

  if ($tooltip.tooltip && !$configEl.data('modern-tooltip')) {
    $tooltip.tooltip?.({
      title,
      trigger,
      placement,
      delay,
      container: 'body',
    })
  } else {
    if ($tooltip.attr('data-controller')?.includes('popover')) return

    $tooltip.attr('data-content', title)
    $tooltip.attr('data-trigger', trigger)
    $tooltip.attr('data-placement', placement)
    $tooltip.attr('data-theme', $tooltip.data('theme') || 'dark-small')
    $tooltip.attr(
      'data-interactive',
      $configEl.attr('data-interactive') || 'false'
    )
    $tooltip.attr('data-animation', $configEl.attr('data-animation') || 'false')
    $tooltip.attr('data-show-delay', $configEl.attr('data-show-delay') || 0)
    $tooltip.attr('data-hide-delay', $configEl.attr('data-hide-delay') || 0)
    $tooltip.attr('data-touch', $configEl.attr('data-touch') || 'false')
    const currentController = $tooltip.attr('data-controller')
    $tooltip.attr('data-controller', classNames(currentController, 'popover'))

    if ($configEl.attr('title')) {
      $configEl.attr('data-title', $configEl.attr('title'))
      $configEl.attr('title', '')
    }
  }
}

function removeTooltip($tooltip) {
  if ($tooltip.tooltip) {
    //TODO: remove bootstrap tooltip
  } else {
    if (!$tooltip.attr('data-controller')?.includes('popover')) return
    const newControllers = $tooltip
      .attr('data-controller')
      .split(' ')
      .filter((controller) => controller != 'popover')
      .join(' ')
    $tooltip.attr('data-controller', newControllers)
  }
}

// Remove any existing tooltips
window.onDomChanged('.tooltip', function () {
  this.remove()
})

window.onDomChanged(
  '[data-behavior~=tooltip],[rel~=tooltip],.s-tooltip',
  function () {
    showTooltip(this, this, {
      trigger: this.data('trigger') || 'hover',
      placement: this.data('placement') || 'top',
      delay: this.data('delay') || 0,
    })
  }
)

/*
 * Add .s-truncate to any element. It'll apply some styles to auto-truncate the text with css.
 * And we'll show a tooltip when it's truncated.
 * You can also have a .s-truncate-attach-tooltip-target class somewhere in the parent and we'll add the tooltip there instead of on this element.
 * You can also specify .s-truncate-show-tooltip-if-hidden to make the condition to show tooltip include the case where the element is completely hidden
 */
window.onDomChanged('.s-truncate', function () {
  const $el = this
  const $tooltip = $el
    .parents('.s-truncate-attach-tooltip-target')
    .addBack()
    .first()
  const showTooltipIfCompletelyHidden = $el.hasClass(
    's-truncate-show-tooltip-if-hidden'
  )
  const $measured = $el.parents('.truncate').addBack()

  // The width reads and the showTooltip/removeTooltip attribute writes go in separate
  // layout_batch phases. Interleaved, each element's write invalidates layout for the next
  // element's read, so a list of .s-truncate rows cost one forced layout apiece.
  measure(() => {
    if (!$el[0]?.isConnected) return

    let shouldShow = false
    $measured.each(function (_, el) {
      shouldShow =
        (showTooltipIfCompletelyHidden && el.clientWidth == 0) ||
        el.scrollWidth > el.clientWidth ||
        // A line-clamped label is cut off at the bottom, not the side
        (el.classList.contains('line-clamp-1') &&
          el.scrollHeight > el.clientHeight + 1)
      if (shouldShow) return false
    })

    mutate(() => {
      if (!$el[0]?.isConnected) return

      if (shouldShow) {
        showTooltip($tooltip, $el, {
          title: $el.attr('title') || $el.text(),
          // Hover only. Bootstrap's default, 'hover focus', keeps the tooltip up while the link has focus: click a
          // truncated vc.obj pill (it opens picture-in-picture), close it, and the tooltip stays stranded on the page.
          trigger: $el.data('trigger') || 'hover',
          placement: $el.data('placement') || 'top',
        })
      } else {
        removeTooltip($tooltip)
      }
    })
  })
})

window.onDomChanged('[rel~=popover],[data-behavior~=popover]', function () {
  this.popover({
    html: true,
    trigger: 'hover',
    container: 'body',
    placement: this.data('placement') || 'auto right',
  })
})

window.onDomChanged('[data-toggle~=popover]', function () {
  this.popover({ html: true, container: 'body' })
})

window.onDomChanged('[rel~=clickover],[data-behavior~=clickover]', function () {
  this.clickover({
    html: true,
    container: 'body',
    width: this.data('clickover-width'),
    sanitize: this.data('sanitize') !== false,
  })
})

$(document).on('click', '[data-toggle=clickover]', function (e) {
  let content
  e.preventDefault()
  const $target = $(e.target).closest('a')
  if ($target.data('content-from') != null) {
    content = $('#' + $target.data('content-from')).html()
  } else if ($target.data('templ') != null) {
    content = window.tmpl($target.data('templ'))
  }

  $target
    .clickover({
      content,
      html: true,
      container: 'body',
      sanitize: $target.data('sanitize') !== false,
    })
    .click((event) => event.preventDefault())
  $target.click()
})

window.onDomChanged('.modal', function () {
  this.attr('tabindex', -1)
})

$(document).on('click', '[data-behavior=generate-coupon-code]', function (e) {
  e.preventDefault()
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVXYZ0123456789'
  let code = ''
  for (let num = 1; num <= 10; num++) {
    const idx = Math.round(Math.random() * (chars.length - 1))
    code += chars[idx]
  }
  // .form-group is a Bootstrap field, .t-field a Tailwind (Boxy) one. The native `input` event lets
  // live previews and unsaved-changes tracking see the new code.
  const input = $(this).closest('.form-group, .t-field').find('input').get(0)
  if (!input) return
  input.value = code
  input.dispatchEvent(new Event('input', { bubbles: true }))
})

$(document).on(
  'click touchstart',
  '[data-behavior=expand_on_click]',
  function (e) {
    let editor
    e.preventDefault()
    const target = $(e.target)
    const elm = target
      .closest('.expanded_content')
      .hide()
      .siblings('.collapsed_content')
      .show()
      .find('[autofocus]')
      .first()
    if ((editor = elm.data('editor'))) {
      editor.focus()
    } else {
      elm.focus()
    }
  }
)

$(document).on('click touchstart', '[data-behavior~=read-more]', function (e) {
  e.preventDefault()
  const $target = $(e.target)
  $target.closest('.read-more-expand').css('max-height', 'none')
  return $target.closest('.read-more').remove()
})

// btn-personalizations is a dropdown toggle button
// that opens a dropdown that could have a long content.
// If it lives inside a #side_panel, the dropdown
// could be cropped and not visible.
// With this code we try to detect if the dropdown
// is about to be opened, and update the overflow style
// of the #side_panel so the dropdown content is visible
window.onDomChanged('.btn-personalizations', function () {
  const $this = $(this)
  const $side_panel = $this.closest('#side_panel')

  // we only care if the button and dropdown live inside a #side_panel
  if ($side_panel.length) {
    $this.on('click', function () {
      // When a btn-personalizations is clicked,
      // they get an "open" class, BUT THAT HAPPENS AFTER THIS CODE RUNS.
      // So if the button ALREADY has "open" class, that means the dropdown
      // is already opened, and is about to be closed.
      // And vice-versa.
      if ($this.hasClass('open')) {
        $side_panel.css('overflow', 'auto') // back to normal
      } else {
        // the dropdown will be opened, so prepare the side_panel
        $side_panel.css('overflow', 'visible')
      }
    })
  }
})

$(document).on('click', '[data-behavior=show-super-admin]', function (e) {
  e.preventDefault()
  $('.super-admin').show()
})

$(document).on('click', '[data-behavior=hide-super-admin]', function (e) {
  e.preventDefault()
  $('.super-admin').hide()
})

$(document).on(
  'click touchstart',
  '[data-behavior~=dropdown-with-partial]',
  function (e) {
    const menu = $(e.target).closest('.dropdown').find('.dropdown-menu')
    defer(() => menu.find('input:visible').first().focus())
  }
)

// data-prompt="Label"
//
// Submits to value of href attribute
//
// Additional data attributes:
//   maxlen:      maximum length. Default no maxlen.
//   value:       Initial value. Default blank.
//   ajax-method: HTTP Method. Default POST.
//   attr:        Name of attribute holding the value. Default 'value'.
//   match:       Regexp the value needs to match
//   match-msg:   Error message if it doesn't match.
//
// preventDefault in capture so Turbo's link observer (bubble) sees
// defaultPrevented and does not visit the href (ENG-31568).
document.addEventListener(
  'click',
  function (e) {
    if (e.target?.closest?.('[data-prompt], [data-prompt-modal]')) {
      e.preventDefault()
    }
  },
  true
)

$(document).on('click', '[data-prompt], [data-prompt-modal]', function (e) {
  e.preventDefault()

  const $target = $(e.target).closest('[data-prompt], [data-prompt-modal]')

  if (document.getElementById('prompt-modal')) {
    e.stopImmediatePropagation()
    window.dispatchEvent(
      new CustomEvent('prompt-modal:open', { detail: { trigger: $target[0] } })
    )
    return
  }

  // Feature flag off — native prompt fallback
  const maxlen = $target.data('maxlen')
  const attr = $target.data('attr') || 'value'
  const match = $target.data('match')
  const match_msg = $target.data('match-msg') || 'Not in the right format'
  const shouldDisable =
    $.rails?.disableFormElement && !!$target.data('disable-with')
  let value = $target.data('value') || ''
  while ((value = prompt($target.data('prompt') || $target.data('prompt-modal-prompt'), value))) {
    if (value === '') {
      break
    }

    if (maxlen && value.length > maxlen) {
      alert(`Too long. Maximum length is ${maxlen}`)
    } else if (match && !value.match(new RegExp(match))) {
      alert(match_msg)
    } else {
      $target.data('value', value)
      shouldDisable && $.rails.disableFormElement($target)
      $.sajax({
        url: $target.data('ajax-url') || $target.attr('href'),
        type: $target.data('ajax-method') || 'POST',
        data: { [attr]: value },
        complete: function () {
          shouldDisable && $.rails.enableFormElement($target)
        },
      })
      break
    }
  }

  if (!value) {
    const behavior = $target.data('behavior')
    if (typeof behavior === 'string' && behavior.includes('disable-on-click')) {
      $target.removeClass('disabled').attr('disabled', false)
    }
  }
})

// The serialized order, plus, when items sit in lanes (an element with data-lane-id, e.g. an automation split's
// paths), lanes[] with each serialized item's lane in the same order. Items without an id aren't serialized, so they
// only act as drop targets.
function sortableData($root, items) {
  const data = $root.sortable('serialize')
  if (!$root[0].querySelector('[data-lane-id]')) return data

  const lanes = $root
    .find(items)
    .filter((_, el) => /(.+)[-=_](.+)/.test(el.id))
    .map((_, el) => {
      const lane = el.parentElement.closest('[data-lane-id]')
      return `lanes[]=${encodeURIComponent(lane ? lane.dataset.laneId : '')}`
    })
    .get()
  return [data, ...lanes].join('&')
}

// data: { behavior: 'sortable', reorder_url: reorder_admin_whatever_path, items: 'div', handle: '.sortable-handle' }
// Optional: cancel (selector a drag never starts from) and distance (px the pointer moves before a drag starts).
// To make the whole thing draggable you have to set handle to false! You'd think you could just specify the same selector as in items, but no.
//
window.onDomChanged('[data-behavior~=sortable]', function () {
  const $this = this
  // The scrollSensitivity is needed for the sticky header, so it starts scrolling up when the user is near the header
  const opts = {
    scrollSensitivity: 150,
  }

  opts.items = $this.data('items') || 'div'

  const handle = this.data('handle')
  if (handle !== null && handle != false && handle !== 'false') {
    opts.handle = this.data('handle') || '.sortable-handle'
  }

  // A whole row as the handle: don't start from controls inside it, and let a click (which barely moves) stay a click.
  if (this.data('cancel')) opts.cancel = this.data('cancel')
  if (this.data('distance')) opts.distance = this.data('distance')

  if (this.data('sortable-placeholder') !== null) {
    opts.placeholder = this.data('sortable-placeholder')
    opts.forcePlaceholderSize = true
  }

  // Lanes (an automation split's paths) are narrower than the row being dragged, whose middle is rarely over them, so
  // go by the pointer there. Checked on each drag: the first split can arrive after the list was set up.
  // With lanes, rows change height as the placeholder moves between columns, so positions are measured on every move
  // instead of once at the start.
  opts.start = function () {
    const lanes = !!$this[0].querySelector('[data-lane-id]')
    $this.sortable('option', { tolerance: lanes ? 'pointer' : 'intersect', refreshPositions: lanes })
  }

  // In lanes the spot a row would drop into can be narrower or wider than where it came from: size the dragged row like
  // that spot, so it shows how it will look there.
  opts.change = function (_event, ui) {
    if (!$this[0].querySelector('[data-lane-id]')) return
    // A spot right under a branch's label sits in a centering column; stretch it to the column's width first. (It
    // copies the row's classes, and auto side margins would keep it from stretching.)
    ui.placeholder.css({ alignSelf: 'stretch', marginLeft: 0, marginRight: 0 })
    ui.helper.css({ width: `${ui.placeholder.outerWidth()}px`, height: 'auto' })
  }

  // A row's own click handlers (e.g. a card that opens its settings when clicked) run before jQuery UI can cancel the
  // click that ends a drag, so swallow that one click here, before it reaches them.
  opts.stop = function () {
    const swallow = (event) => {
      event.stopPropagation()
      event.preventDefault()
    }
    document.addEventListener('click', swallow, { capture: true, once: true })
    setTimeout(
      () => document.removeEventListener('click', swallow, { capture: true }),
      0
    )
  }

  opts.update = function () {
    // Automatically update 'position' hidden input, for models using acts_as_list
    const positions = $this.find('[data-behavior=sortable-position]')
    if (positions[0]) {
      let index = 0
      positions.each((_, pos) => {
        index += 1
        $(pos).val(index)
      })
    }

    // Submit to controller when relevant
    const url = $this.data('reorder-url')
    if (url) {
      $this.sortable('disable')
      $.ajax({
        url,
        data: sortableData($this, opts.items),
        beforeSend() {
          $this[0].dispatchEvent(
            new CustomEvent('sortable:beforeSend', { bubbles: true })
          )
        },
        success(data) {
          // Full list re-renders replace/update this node and re-init sortable via onDomChanged.
          // Surgical responses (no touch of the sortable root) must re-enable instead.
          const root = $this[0]
          const mapTouchesRoot = (map) =>
            !!(
              map &&
              Object.keys(map).some((id) => {
                const el = document.getElementById(String(id).replace(/^#/, ''))
                return el === root || (el && el.contains(root))
              })
            )
          const replacesList =
            !!data &&
            (mapTouchesRoot(data.update) ||
              mapTouchesRoot(data.replace) ||
              mapTouchesRoot(data.morph_replace) ||
              mapTouchesRoot(data.extract_and_replace))
          if (replacesList) {
            $this.sortable('destroy')
          }
          window.AjaxHelper.doUpdate(data)
          if (!replacesList && document.body.contains(root)) {
            $this.sortable('enable')
          }
        },
        error() {
          window.AjaxHelper.error(`Error saving your sort order`)
          $this.sortable('enable')
        },
        complete() {
          $this[0].dispatchEvent(
            new CustomEvent('sortable:complete', { bubbles: true })
          )
        },
      })
    }
  }

  this.sortable(opts)

  // Automation splits (rows in lanes) need two adjustments to jQuery UI's pointer matching:
  // - A split is a sortable row that holds its branches' rows. While the pointer is over its branches, only those rows
  //   count; otherwise the gaps between branch columns land on the split itself and the placeholder jumps out to the
  //   main line and back.
  // - jQuery UI only matches while the pointer moves up or down. Moving sideways into another branch has no vertical
  //   direction, so there it goes by which half of the row the pointer is over.
  const sortable = this.sortable('instance')
  const intersectsWithPointer = sortable._intersectsWithPointer
  sortable._intersectsWithPointer = function (item) {
    if (!this.element[0].querySelector('[data-lane-id]')) return intersectsWithPointer.call(this, item)

    const x = this.positionAbs.left + this.offset.click.left - window.scrollX
    const y = this.positionAbs.top + this.offset.click.top - window.scrollY
    const inside = (rect) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
    const row = item.item[0]
    const branches = row.querySelector('[data-split-paths]')
    if (branches && inside(branches.getBoundingClientRect())) return false

    const result = intersectsWithPointer.call(this, item)
    const rect = row.getBoundingClientRect()
    if (result || !inside(rect)) return result
    return y < rect.top + rect.height / 2 ? 1 : 2
  }
})

$(document).on('click', '[data-toggle=collapse]', (e) => e.preventDefault())

$(document).on(
  'click',
  '[data-behavior=asset-grab-current-frame]',
  function (e) {
    e.preventDefault()
    const target = $(e.target).closest('a')
    const video = $('.' + target.data('video-class'))
      .first()
      .find('video')
    const seconds =
      video[0]?.currentTime || video.data('videojs-player').currentTime()
    const type = target.data('type')
    if (seconds != null) {
      $.sajax({
        url: target.attr('href'),
        data: {
          seconds,
          type,
        },
      })
    } else {
      alert('Please start the video and pause at the frame you want.')
    }
  }
)

$(document).on(
  'click touchstart',
  '[data-behavior=hide-admin-only]',
  function (e) {
    e.preventDefault()
    return $('.admin-only').toggle()
  }
)

window.onDomChanged('[data-behavior=observe]', function () {
  const $this = this
  const interval = $this.data('interval') ? $this.data('interval') * 1000 : 1000
  const event = $this.data('event') ? $this.data('event') : 'keyup'
  const on_elm = $this.data('on') ? $($this.data('on')) : $this

  const callback = () =>
    $.sajax({
      url: $this.data('ping-url'),
      error: () => {},
    })

  const observer = new window.Observer(callback, { interval })

  on_elm.on(`${event}.observer`, () => observer.modified())
})

window.onDomChanged('.modal.start-in', function () {
  $(this).modal('show')
})

$(document).on('click', 'a[data-do-update]', function (e) {
  e.preventDefault()
  const elm = $(e.target).closest('a')
  const update = elm.data('do-update')
  window.AjaxHelper.doUpdate(update)
})

// If you mark table cells with class 'drag-static', we will swap the contents of those cells, so they effectively remain in place
// Useful for things like position number, or schedule dates in the case of newsletters

const setupTableDnD = function (elm) {
  // Store all static cells in an array
  const static_content = {}
  $('tr', elm).each(function (i, tr) {
    if (!$(tr).hasClass('nodrop') && !$(tr).parent().is('thead')) {
      static_content[i] = {}
      $('td.drag-static, td.drag-static-inside', tr).each(function (j, td) {
        let $elem = $(td)
        // Note: Even if a .drag-static-inside column doesn't have .drag-static element inside it we still need to set an undefined value.
        if ($elem.hasClass('drag-static-inside')) {
          $elem = $elem.find('.drag-static')
        }
        static_content[i][j] = $elem.html()
      })
    }
  })

  $(elm).tableDnD({
    dragHandle: '.drag-handle',
    onDragClass: 'tr-dragging',
    onDrop() {
      return $.sajax({
        url: elm.data('href'),
        data: elm.tableDnDSerialize(),
      })
    },
    onMove() {
      $(elm).find('.tr-dragging').finish()
      $('tr', elm).each((i, tr) =>
        $('td.drag-static, td.drag-static-inside', tr).each(function (j, td) {
          if (static_content[i] && static_content[i][j]) {
            let $elem = $(td)
            if ($elem.hasClass('drag-static-inside')) {
              $elem = $elem.find('.drag-static')
            }
            if ($elem) {
              $elem.html(static_content[i][j])
            }
            $elem.changed()
          }
        })
      )
      return true
    },
  })
}

window.onDomChanged('[data-behavior~=table-dnd]', function () {
  setupTableDnD(this)
})

//#
// Autoload tab from remote
//

$(document).on('click', 'a[data-load-tab]', function (e) {
  const tab = $(e.target)
  const target = $(tab.attr('href'))

  if (target.find('.loading_indicator')[0] != null) {
    return $.ajax({
      url: tab.data('load-tab'),
      type: 'GET',
      success(data) {
        return target.html(data.content).changed()
      },
      error: window.AjaxHelper.errorFunc,
    })
  }
})

//#
// Disable clicking on a link with disabled="disabled" attribute.
// We use this when activating. It works fine in Safari, but Chrome seems to let the click go through.
//

$(document).on('click', 'a[disabled=disabled]', (e) => e.preventDefault())

window.onDomChanged(
  "[data-behavior~=track-dirty-text-inputs] input[type='text'], [data-behavior~=track-dirty-text-inputs] textarea",
  function () {
    const $this = $(this)
    // Capture the baseline only once per element. Later .changed()/onDomChanged
    // passes would re-baseline to the current (possibly edited) value and drop
    // the unsaved-changes confirm on modal close.
    if ($this.data('dirty-tracking')) return
    $this.data('initial-input-value', $this.val())
    $this.data('dirty-tracking', true)
  }
)

// Currency inputs reformat their own text on focus and blur ('-1,250.00' <-> '-1250.00'),
// so they count as changed only when the amount changes.
const currencyAmount = (value) =>
  Number(String(value ?? '').replace(/[\s,]/g, '') || 0)

const inputChanged = function (input) {
  const initial = $(input).data('initial-input-value')
  const current = $(input).val()
  if (initial === current) return false
  if ($(input).hasClass('currency_field')) {
    const before = currencyAmount(initial)
    const after = currencyAmount(current)
    if (!Number.isNaN(before) && !Number.isNaN(after)) return before !== after
  }
  return true
}

const hasDirtyInputs = function (container) {
  let dirty = false
  container.find('input[type="text"], textarea').each(function () {
    if (!dirty && $(this).data('dirty-tracking') && inputChanged(this)) {
      dirty = true
    }
  })
  return dirty
}

const confirmDiscardChanges = () =>
  confirm(
    'You have unsaved changes. Are you sure you want to close this window?'
  )

//#
// Makes the modal close click trigger an additional confirmation if the modal has dirty text inputs
$(document).on('hide-with-trigger-event.bs.modal', function (e) {
  const isUserEvent = e.triggerEvent?.originalEvent?.isTrusted
  const modal = $(e.target).closest('.modal')
  if (modal && hasDirtyInputs(modal) && isUserEvent) {
    if (!confirmDiscardChanges()) {
      e.preventDefault()
    }
  }
})

// Same for Tailwind modals carrying track-dirty-text-inputs (ModalComponent form: adds it). modal:beforeClose only fires for user closes.
document.addEventListener('modal:beforeClose', (e) => {
  const modal = e.target
  if (!modal.matches?.('.js-modal[data-behavior~=track-dirty-text-inputs]')) {
    return
  }
  if (!hasDirtyInputs($(modal))) return
  if (!confirmDiscardChanges()) {
    e.preventDefault()
    return
  }
  keepFocusAfterNativeDialog()
})

// The confirm() above blurs the window, and that blur arrives only after the dialog has closed and
// put focus back on its opener. Openers with a tooltip then lose focus to <body>: tippy blurs its
// reference element on window blur. Bootstrap returned focus after its 300ms fade, so it never hit
// this. Put focus back once the window has it again, unless it went somewhere else meanwhile.
const keepFocusAfterNativeDialog = () => {
  // Runs once the close that dispatched modal:beforeClose has finished and refocused the opener
  queueMicrotask(() => {
    const doc = document
    const win = window
    const target = doc.activeElement
    if (!target || target === doc.body) return
    const restore = () => {
      const active = doc.activeElement
      if ((!active || active === doc.body) && target.isConnected) {
        target.focus({ preventScroll: true })
      }
    }
    win.addEventListener('focus', restore, { once: true })
    setTimeout(restore, 0)
    setTimeout(() => win.removeEventListener('focus', restore), 1000)
  })
}
